<script setup></script>

<template>
  <div class="row-actions">
    <slot />
  </div>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/palette" as p;

.row-actions {
  display: inline-flex;
  align-items: center;
  height: 28px;
  border: 1px solid c.$border-subtle;
  border-radius: 6px;
  background: c.$background-ui;
  overflow: hidden;

  // Dividers between all direct children (must come FIRST, before resets)
  > :deep(* + *) {
    border-inline-start: 1px solid c.$border-subtle;
  }

  // Direct <button> children — the inline action buttons
  // Reset only non-divider borders so the * + * rule above sticks
  > :deep(button) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 100%;
    background: none;
    border-top: 0;
    border-bottom: 0;
    border-inline-end: 0;
    cursor: pointer;
    color: p.$brand-60;
    padding: 0;
    outline: revert;
    transition:
      background-color 100ms ease,
      color 100ms ease;

    &:hover {
      background-color: c.$border-subtle;
      color: c.$main;
    }

    &:disabled {
      opacity: 0.4;
      cursor: default;
      pointer-events: none;
    }
  }

  // The dropdown container — fill available height, no extra sizing
  > :deep(.dropdown) {
    display: flex;
    align-items: stretch;
    height: 100%;
    border-top: 0;
    border-bottom: 0;
    border-inline-end: 0;

    // The dropdown trigger button — match group height, auto width
    .dropdown-trigger {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: auto;
      background: none;
      border: none;
      cursor: pointer;
      color: p.$brand-50;
      padding: 0;
      outline: revert;
      transition:
        background-color 100ms ease,
        color 100ms ease;

      &:hover {
        background-color: c.$border-subtle;
        color: c.$main;
      }

      // The inner span (trigger slot content)
      > span {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
}
</style>

<!--
  Reveal-on-row-hover. Not scoped: it keys off the table-row ancestor, which a
  scoped style on .row-actions can't select. Guarded by .row-actions so it only
  affects rows that have row actions.
-->
<style lang="scss">
// Right-align the action cell so the persistent control stays pinned to the
// edge while revealed buttons expand leftward. The `.table` ancestor is needed
// to outrank DataTableV2's scoped `td { text-align: left }`.
.table tbody tr td:has(> .row-actions) {
  text-align: right;
}

tbody tr {
  .row-actions > :not(:last-child) {
    display: none;
  }

  // Drop the divider that would otherwise float left of the lone visible
  // control while the inline buttons are hidden.
  .row-actions > .dropdown {
    border-inline-start-width: 0;
  }

  &:hover .row-actions > :not(:last-child),
  &:focus-within .row-actions > :not(:last-child) {
    display: flex;
  }

  &:hover .row-actions > .dropdown,
  &:focus-within .row-actions > .dropdown {
    border-inline-start-width: 1px;
  }
}
</style>
